'use client';

import {
  attendantsAge,
  crudNotFound,
  requestDuoDateSearchPlaceholder,
} from '@bells/i18n';
import {
  AttendantSearchResponse,
  AttendantSearchSortBy,
  ThreesomeStatus,
} from '@bells/schema/models/attendant';
import { RequestForm } from '@bells/schema/models/request';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandList,
} from '@bells/ui/components/command';
import { useEffect, useMemo, useState } from 'react';

import attendantContract from '@bells/api/attendants/frontend';
import { cn } from '@bells/core/lib/utils';
import { generateRestClient } from '@bells/core/rest/react';
import { Command as CommandPrimitive } from 'cmdk';
import { first } from 'lodash';
import { Calendar, Loader2, MapPin, Ruler } from 'lucide-react';
import { useDebounce } from 'use-debounce';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@bells/ui/components/avatar';
import { getInitials } from '@bells/core/lib/initals';

const systemRestClient = generateRestClient(attendantContract);

export const DuoDateResultItem = ({
  item,
  duration,
  selected,
  onSelect,
}: {
  item: AttendantSearchResponse;
  duration?: number;
  selected: boolean;
  onSelect: (item: AttendantSearchResponse | undefined) => void;
}) => {
  return (
    <CommandPrimitive.Item
      value={item.slug}
      onSelect={() => onSelect(item)}
      className={cn(
        't m-1 my-2 flex h-max cursor-pointer select-text items-center gap-2 rounded-lg border p-2 px-3 ring-primary ring-offset-2 transition duration-150 hover:bg-muted data-[active="true"]:border-input data-[active="true"]:ring'
      )}
      data-active={selected}
      key={`suggestion-${item.slug}`}
      onMouseDown={(e) => e.preventDefault()}
    >
      <div className='flex w-full items-start gap-2'>
        <Avatar>
          <AvatarFallback>{getInitials(item.name)}</AvatarFallback>
          <AvatarImage src={item.defaultMedia?.xs}></AvatarImage>
        </Avatar>
        <div className='flex w-full flex-1 flex-col items-center gap-1'>
          <div className='flex w-full justify-between gap-2'>
            <div className='flex w-full flex-1 items-center gap-1 font-medium'>
              {item.name}
            </div>
            {item && (
              <span className='text-muted-foreground'>{item.price} €</span>
            )}
          </div>

          <span className='flex w-full flex-wrap gap-4 text-sm opacity-70'>
            {item.locations?.map((df, index) => {
              return <div key={`loc-${index}`}>{df.name}</div>;
            })}

            {item.height && (
              <span className='whitespace-nowrap break-keep'>
                <Ruler className='mr-1 inline-block h-4 w-4'></Ruler>
                {`${item.height} cm`}
              </span>
            )}

            {item.age && (
              <span className='whitespace-nowrap break-keep'>
                <Calendar className='mr-1 inline-block h-4 w-4'></Calendar>
                {attendantsAge({ age: item.age ?? '' })}
              </span>
            )}

            {item.distance && (
              <span className='whitespace-nowrap break-keep'>
                <MapPin className='mr-1 inline-block h-4 w-4'></MapPin>
                {item.distance} km
              </span>
            )}
          </span>
        </div>
      </div>
    </CommandPrimitive.Item>
  );
};
export const RequestDuoDateResults = ({
  onSelect,
  term,
  placeholder,
  duration,
  address,
  threesome,
  value,
  exclude,
}: {
  onSelect: (val: AttendantSearchResponse | undefined) => void;
  term?: string;
  exclude: string;
  placeholder?: string;
  value?: string;
  duration?: number;
  address?: RequestForm['address'];
  threesome?:
    | ThreesomeStatus.ESCORT_PARTNER_BISEXUAL
    | ThreesomeStatus.ESCORT_PARTNER_HETERO;
}) => {
  const [searchInput, setSearchInput] = useState<string>(term ?? '');

  const [debouncedSearchInput, setDebounceSearchInput] = useDebounce(
    searchInput,
    150
  );

  useEffect(() => {
    setSearchInput(term ?? '');
    setDebounceSearchInput(term ?? '');
  }, [term]);

  const { data, isLoading } = systemRestClient.search.useQuery({
    queryKey: [
      'duoDate',
      debouncedSearchInput,
      address?.lng,
      address?.lat,
      duration,
      threesome,
      exclude,
    ],
    queryData: {
      query: {
        services: threesome ? [threesome] : [],
        attendantName: debouncedSearchInput,
        sortBy: AttendantSearchSortBy.DISTANCE_ASC,
        country: address?.country,
        lng: address?.lng,
        lat: address?.lat,
        duration: duration,
        exclude: [exclude],
        limit: 10,
        includePrices: true,
      },
    },
    select: (df) => df.body,
  });

  return (
    <Command
      shouldFilter={false}
      className='h-full w-full flex-1 bg-transparent'
    >
      <CommandInput
        value={searchInput}
        className='font-medium'
        onValueChange={(val) => setSearchInput(val)}
        placeholder={placeholder}
      />

      <CommandList className='h-full max-h-max bg-transparent'>
        <CommandEmpty>
          {!isLoading && data?.items?.length === 0 && (
            <div className='flex items-center justify-center py-4'>
              {crudNotFound()}
            </div>
          )}
        </CommandEmpty>
        <CommandGroup className={cn('relative h-auto', { hidden: !isLoading })}>
          <div className='relative flex items-center justify-center gap-2 px-2 py-2 uppercase text-muted-foreground'>
            <Loader2 className='h-8 w-8 animate-spin' />
          </div>
        </CommandGroup>
        {data?.items && !isLoading && data?.items?.length > 0 && (
          <CommandGroup heading={undefined}>
            {data?.items.map((item) => {
              return (
                <DuoDateResultItem
                  key={`suggestion-${item.slug}`}
                  item={item}
                  duration={duration}
                  selected={value === item.slug}
                  onSelect={(val) => {
                    if (val?.slug == value) {
                      onSelect(undefined);
                    } else {
                      onSelect(val);
                    }
                  }}
                />
              );
            })}
          </CommandGroup>
        )}
      </CommandList>
    </Command>
  );
};

export const DuoDateSelect = ({
  threesome,
  onChange,
  duration,
  address,
  value,
  exclude,
}: {
  threesome:
    | ThreesomeStatus.ESCORT_PARTNER_BISEXUAL
    | ThreesomeStatus.ESCORT_PARTNER_HETERO;
  onChange: (date: AttendantSearchResponse | undefined) => void;
  duration?: number;
  address?: RequestForm['address'];
  value?: string;
  exclude: string;
}) => {
  const [selectedDate, setSelectedDate] = useState<
    AttendantSearchResponse | undefined
  >(undefined);

  useEffect(() => {
    onChange(selectedDate);
  }, [selectedDate]);

  return (
    <RequestDuoDateResults
      threesome={threesome}
      duration={duration}
      value={value}
      exclude={exclude}
      placeholder={requestDuoDateSearchPlaceholder()}
      onSelect={(value) => {
        setSelectedDate(value);
      }}
      address={address}
    ></RequestDuoDateResults>
  );
};
